import React, { useCallback } from 'react';
import { Box, Typography, Tabs, Tab, Paper } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardIcon      from '@mui/icons-material/Dashboard';
import SearchIcon          from '@mui/icons-material/Search';
import BugReportIcon       from '@mui/icons-material/BugReport';
import ScienceIcon         from '@mui/icons-material/Science';
import CalendarMonthIcon   from '@mui/icons-material/CalendarMonth';
import AssessmentIcon      from '@mui/icons-material/Assessment';
import LightbulbIcon       from '@mui/icons-material/Lightbulb';
import ServiceOperationsOverviewTab from './ServiceOperationsOverviewTab';
import PlatformInvestigationTeamTab, { PLATFORM_INVESTIGATION_TEAMS } from './PlatformInvestigationTeamTab';
import ServiceOperationsSyntheticTab from './ServiceOperationsSyntheticTab';
import ProductionDefects from '../public/ProductionDefects';
import ActivitiesTab from './ActivitiesTab';
import WorkstreamIdeasTab from './WorkstreamIdeasTab';
import StabilityImprovementsTab from '../reports/StabilityImprovementsTab';

const TABS = ['overview', 'platform-investigation', 'production-bugs', 'synthetic-tests', 'activities', 'workstream-ideas', 'reports'];

const TAB_DEFS = [
  { key: 'overview',               icon: <DashboardIcon     sx={{ fontSize: 16 }} />, label: 'Overview'               },
  { key: 'platform-investigation', icon: <SearchIcon        sx={{ fontSize: 16 }} />, label: 'Platform Investigation' },
  { key: 'production-bugs',        icon: <BugReportIcon     sx={{ fontSize: 16 }} />, label: 'Production Bugs'        },
  { key: 'synthetic-tests',        icon: <ScienceIcon       sx={{ fontSize: 16 }} />, label: 'Synthetic Tests'        },
  { key: 'activities',             icon: <CalendarMonthIcon sx={{ fontSize: 16 }} />, label: 'Activities'             },
  { key: 'workstream-ideas',       icon: <LightbulbIcon     sx={{ fontSize: 16 }} />, label: 'Workstream Suggestions' },
  { key: 'reports',                icon: <AssessmentIcon    sx={{ fontSize: 16 }} />, label: 'Reports'                },
];

const PlatformInvestigationView = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const tabParam = new URLSearchParams(location.search).get('tab');
  const activeTab = TABS.includes(tabParam) ? tabParam : 'overview';

  const goToTab = useCallback((key) => {
    navigate(`/?section=service-operations&tab=${key}`, { replace: false });
  }, [navigate]);

  const handleTabChange = (_, idx) => goToTab(TABS[idx]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ color: '#1D4434', mb: 0.5 }}>
          Service Operations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Platform health, incident response, and quality metrics
        </Typography>
      </Box>

      {/* Top-level tabs */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={TABS.indexOf(activeTab)}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: '1px solid rgba(0,0,0,0.12)', px: 1 }}
        >
          {TAB_DEFS.map(t => (
            <Tab
              key={t.key}
              icon={t.icon}
              iconPosition="start"
              label={t.label}
              sx={{ minHeight: 48, textTransform: 'none', fontWeight: activeTab === t.key ? 700 : 400 }}
            />
          ))}
        </Tabs>
      </Paper>

      {activeTab === 'overview'               && <ServiceOperationsOverviewTab onNavigate={goToTab} />}
      {activeTab === 'platform-investigation' && <PlatformInvestigationTeamTab teams={PLATFORM_INVESTIGATION_TEAMS} />}
      {activeTab === 'production-bugs'        && <ProductionDefects />}
      {activeTab === 'synthetic-tests'        && <ServiceOperationsSyntheticTab />}
      {activeTab === 'activities'             && <ActivitiesTab />}
      {activeTab === 'workstream-ideas'       && <WorkstreamIdeasTab />}
      {activeTab === 'reports'               && <StabilityImprovementsTab />}
    </Box>
  );
};

export default PlatformInvestigationView;
